// ─── System alerts (hub 2026-10-09) ───────────────────────────────────────────
//
// agent_notifications rows that have NO conversation: the workflow alerts (portal_verify_failed,
// daily_filled_stopped), brain_eval_failed, staff_sms_not_configured, invalid_phone and the lead-level codes --
// and ANY future code, with no list to update: the rule is "no conversation_id", never a reason list. Until this
// panel they had nowhere to be seen or closed; the Conversations tab only shows alerts attached to a thread.
//
// "Mark handled" writes the same shape the worker's /staff-note writes on a thread's escalations (status resolved,
// handled_by, handled_at), on ONE row, and only while that row is still open and still conversation-less.
//
// PURE, like the other ./lib modules, so it can be pinned without a DOM.
import { isEscalationHandled } from './escalationState';

export const SYSTEM_ALERT_HANDLED_BY = 'crm';
// The worker's */15 tick closes stale invalid_phone alerts with this (OVERNIGHT-AGENTS-2026-10-10 §2).
export const AUTO_HANDLED_BY = 'auto';

// Labels for the codes we know; any other code is shown as itself.
export const SYSTEM_ALERT_LABELS = {
  portal_verify_failed: "Onboarding portal's daily check failed",
  daily_filled_stopped: 'Daily filled-jobs run stopped',
  brain_eval_failed: 'Brain eval failed',
  staff_sms_not_configured: 'Staff SMS not configured',
  invalid_phone: 'Lead with an untextable phone',
  staff_email_failed: "A new lead's staff email failed to send",   // DAY-DIRECTSTAFF-2026-10-28: the leads worker, once its Formspree fallback is off
};

export function systemAlertLabel(reason) {
  const r = String(reason == null ? '' : reason);
  return SYSTEM_ALERT_LABELS[r] || r || '(no reason)';
}

/** Rulings 2026-10-14 (4): the short alert id (first 8 characters) shown in grey -- the same 8 the ?alert= link, the
 *  Worker logs and the reports use, so a row can be matched to them at a glance. '' when there is no id. */
export function shortAlertId(n) {
  const id = String((n && n.id) == null ? '' : n.id).trim();
  return id ? id.slice(0, 8) : '';
}

/** A system alert = a notification with no conversation. */
export function isSystemAlert(n) {
  return !!n && (n.conversation_id === null || n.conversation_id === undefined || n.conversation_id === '');
}

const ts = (n) => { const t = Date.parse(n && n.created_at); return Number.isFinite(t) ? t : 0; };

/** System alerts, newest first. Open only unless showHandled. Never mutates its input. */
export function systemAlerts(notifications, { showHandled = false } = {}) {
  return (notifications || [])
    .filter((n) => isSystemAlert(n) && (showHandled || !isEscalationHandled(n)))
    .slice()
    .sort((a, b) => ts(b) - ts(a));
}

// ALERT NOISE (OVERNIGHT-AGENTS-2026-10-10 §2): low-priority rows are still listed, under their own collapsed group,
// but only priority >= normal counts on the badge. A missing or unknown priority counts -- never hide what we cannot
// rank.
export function isLowPriority(n) {
  return String((n && n.priority) == null ? '' : n.priority).trim().toLowerCase() === 'low';
}

/** -> { main, low }: the listed alerts split by priority, each newest first. */
export function systemAlertGroups(notifications, opts = {}) {
  const all = systemAlerts(notifications, opts);
  return { main: all.filter((n) => !isLowPriority(n)), low: all.filter(isLowPriority) };
}

/** The sidebar badge: open system alerts of priority normal or above. */
export function openSystemAlertCount(notifications) {
  return systemAlerts(notifications).filter((n) => !isLowPriority(n)).length;
}

/** What "Mark all low-priority handled" acts on: open, conversation-less, low-priority ids. */
export function lowBulkTargets(notifications) {
  return systemAlerts(notifications).filter(isLowPriority).map((n) => n.id).filter(Boolean);
}

/** The pill text for a handled alert: the worker's auto-close says so. */
export function systemAlertHandledLabel(n) {
  return n && n.handled_by === AUTO_HANDLED_BY ? 'auto-closed' : 'handled';
}

/**
 * Mark many handled, ONE AT A TIME through `markOne` -- the single button's own write, so each row gets exactly the
 * same update. A failure is counted and the rest still run. -> { handled, already, failed }.
 */
export async function markManyHandled(ids, markOne) {
  const out = { handled: 0, already: 0, failed: 0 };
  for (const id of ids || []) {
    try {
      const r = await markOne(id);
      if (r === 'handled') out.handled++; else out.already++;
    } catch (_) {
      out.failed++;
    }
  }
  return out;
}

export function bulkResultText({ handled, already, failed }) {
  const parts = [`${handled} marked handled`];
  if (already) parts.push(`${already} already handled`);
  if (failed) parts.push(`${failed} failed`);
  return parts.join(', ') + '.';
}

// BULK CLOSE (NIGHT-AGENTS-2026-10-16 §2): tick several alerts -> Mark handled, with one note for all of them. The note
// is written to agent_notifications.handled_note, a nullable column that does not exist until the proposed migration
// (migrations/2026-10-16-agent-notifications-handled-note.sql) is applied. Until then the probe says so, the note box
// is greyed with that reason, and the write is exactly today's -- never a write naming a missing column.
export const ALERT_NOTE_MAX = 500;
export const ALERT_NOTE_COLUMN = 'handled_note';

/** One line, trimmed, at most ALERT_NOTE_MAX characters; '' -> null. */
export function normalizeAlertNote(note) {
  if (note === null || note === undefined) return null;
  const one = String(note).replace(/\s+/g, ' ').trim();
  return one ? Array.from(one).slice(0, ALERT_NOTE_MAX).join('') : null;
}

/** Does agent_notifications.handled_note exist? A select naming it errors when it does not. Any failure -> false. */
export async function probeAlertNoteColumn(client) {
  try {
    const { error } = await client.from('agent_notifications').select(ALERT_NOTE_COLUMN, { head: true }).limit(0);
    return !error;
  } catch (_) {
    return false;
  }
}

/** The handled write. The note rides along ONLY when the column exists (noteColumn true) and there is a note. */
export function handledPatch(atIso, note = null, noteColumn = false) {
  const patch = { status: 'resolved', handled_by: SYSTEM_ALERT_HANDLED_BY, handled_at: atIso };
  const n = normalizeAlertNote(note);
  if (noteColumn === true && n) patch[ALERT_NOTE_COLUMN] = n;
  return patch;
}

/** The local mirror of a Mark handled, so the panel and badge are right without a reload. New array. */
export function applyHandled(notifications, id, atIso, note = null, noteColumn = false) {
  return (notifications || []).map((n) => (n && n.id === id && isSystemAlert(n) && !isEscalationHandled(n)
    ? { ...n, ...handledPatch(atIso, note, noteColumn) } : n));
}

/** What a bulk close acts on: the ticked ids that are still open system alerts (a stale tick is dropped, not written). */
export function bulkCloseTargets(notifications, selectedIds) {
  const want = new Set(selectedIds || []);
  return systemAlerts(notifications).filter((n) => want.has(n.id)).map((n) => n.id);
}

export function bulkCloseConfirmText(count, note) {
  const n = normalizeAlertNote(note);
  return `Mark ${count} alert(s) handled?` + (n ? `\n\nNote: ${n}` : '') + '\n\nNothing is sent to anyone; each alert is closed as if you pressed its own Mark handled.';
}

/**
 * Mark one system alert handled. -> 'handled' | 'already' (someone else got there first, or it is no longer a
 * conversation-less open row). Throws on a store error, so the caller shows it and changes nothing locally.
 */
// DAY-DIRECTSTAFF-2026-11-11 §2: Today's "People waiting" -- close ANY open hand-off (a thread's or not) with a reason, and say WHO:
// handled_by = the staff email (the hub counts an email as a staff close; 'crm' / 'offline' read as the system), the reason as the note.
/** -> 'handled' | 'already' (someone or something closed it first) */
export async function closeHandoff(client, id, { atIso = new Date().toISOString(), reason = '', by = '', noteColumn = false } = {}) {
  if (!id) throw new Error('no alert id');
  const patch = { status: 'resolved', handled_by: String(by || '').trim() || SYSTEM_ALERT_HANDLED_BY, handled_at: atIso };
  const n = normalizeAlertNote(reason);
  if (noteColumn === true && n) patch[ALERT_NOTE_COLUMN] = n;
  const { data, error } = await client.from('agent_notifications').update(patch).eq('id', id).eq('status', 'open').select('id');
  if (error) throw error;
  return Array.isArray(data) && data.length ? 'handled' : 'already';
}

export async function markSystemAlertHandled(client, id, atIso, note = null, noteColumn = false) {
  if (!id) throw new Error('no alert id');
  const { data, error } = await client
    .from('agent_notifications')
    .update(handledPatch(atIso, note, noteColumn))
    .eq('id', id)
    .eq('status', 'open')
    .is('conversation_id', null)
    .select('id');
  if (error) throw error;
  return Array.isArray(data) && data.length ? 'handled' : 'already';
}
